import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Circle2d } from '@/editor/math/circle2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '../../math/Line2d';
import { LineSeg2d } from '../../math/LineSeg2d';
import { Point2dUtils } from '../../math/Point2dUtils';
import { arcThresholdValue, thresholdValue } from '@/editor/math/const';
import { intersectCommonShape, intersectRayAndLineSeg } from '@/editor/math/shape/intesection';
import { getTangentDirection } from '@/editor/math/shape/tangent';
import { normalizeAngle } from '@/editor/math/utils';
import { dot, getAngle, getDirectionFromAngle, opposite, rotate } from '@/editor/math/vector';
import { IShapeSeg2d } from '@/editor/types/shape';
import { HandleType } from '../wallShape/calculateHelper';
import { isInsideBand, signedSideOfCenterline } from './geometry';

export interface IExtendedSegIntersectSegsParams<T = IShapeSeg2d, K = IShapeSeg2d> {
  mainSeg: T;
  subSegInfos: { seg: K; wallId: string }[];
  handleType: HandleType;
  isLeft?: boolean; // mainSeg 是主墙左偏移边还是右偏移边（弧形主墙选交点的"穿进/穿出"侧需据此反转）
  // wallId → 相邻墙中线(Circle2d/Line2d)，用于判断交点落在相邻墙的哪一侧。
  subCenterMap?: Map<string, Circle2d | Line2d>;
  // 主墙本体参考点(中线中点)：交点须与它在相邻墙中线的同一侧才保留。
  bodyRef?: IPoint2d;
  // 主墙端点是否落在相邻墙内部(T 型)。true：直墙相邻墙也按"本体同侧"拼边，避免嵌入墙体；false(端点端点相连)：直墙用 dot 斜接。
  pointOnWall?: boolean;
  // 房间/结构墙(厚 0)中线边界：落在其外侧(与 bodyRef 异侧)的弧交点一律剔除，避免端盖戳出房间。
  boundaryLines?: Line2d[];
  // 相连厚墙的实心带(中线 seg + 半宽)：埋在其内部的弧交点一律剔除(合法端盖顶点不会落在另一条墙体内)。
  occluders?: { seg: IShapeSeg2d; halfWidth: number }[];
  // 本条偏移边【在接头那头的端点】是否已经埋在别的东西里(厚墙实心带 / 厚 0 边界围出的实心区)。
  // 埋着 → 端盖点只能取沿 body 前向的出口，不能取接头身后的掠过点。由 calPointWallIntersection 算好传入。
  originEnclosed?: boolean;
  // 子边「延长形态」求交时允许越过子边端点的绝对长度(≈2×主墙墙厚)。见 extendedArcIntersectSegments 内注释。
  maxOvershoot?: number;
  // 这些墙的子边是【精确边界】(厚 0 房间/结构线、薄片薄条的边)：它们没有端盖、也没有斜接填充，
  // 线段到头就是到头，越过端点那一侧根本没有材料。对它们不给越界余量，避免把延长线上的点当成真切点。
  exactSegWallIds?: Set<string>;
}

export interface IExtendedSegIntersectSegsResult {
  point: IPoint2d;
  index: number;
}

export function extendedSegIntersectSegs(
  params: IExtendedSegIntersectSegsParams
): IExtendedSegIntersectSegsResult | null {
  const { mainSeg, subSegInfos } = params;

  const allSegIsStraight = subSegInfos.every(({ seg }) => seg instanceof LineSeg2d) && mainSeg instanceof LineSeg2d;

  if (allSegIsStraight) {
    return extendedLineLineIntersectSegments(params as IExtendedSegIntersectSegsParams<LineSeg2d, LineSeg2d>);
  } else if (mainSeg instanceof LineSeg2d) {
    return extendedLineIntersectSegments(params as IExtendedSegIntersectSegsParams<LineSeg2d>);
  } else if (mainSeg instanceof ArcSeg2d) {
    return extendedArcIntersectSegments(params as IExtendedSegIntersectSegsParams<ArcSeg2d>);
  }

  return null;
}

// 精确子边(厚 0 边界)允许的越界量：只覆盖浮点噪声，不是斜接余量。
const EXACT_SEG_TOL = 0.01;

interface IIntersectPointInfo {
  point: IPoint2d;
  index: number;
  angle: number;
  wallId: string;
  intersectCount: number;
}

// 求弧形线段与线段数组相交点，返回最邻近点，startAsOrigin为true表示求起点相邻点
function extendedArcIntersectSegments(
  params: IExtendedSegIntersectSegsParams<ArcSeg2d>
): IExtendedSegIntersectSegsResult | null {
  const {
    mainSeg: mainArc,
    subSegInfos,
    handleType,
    isLeft = false,
    subCenterMap,
    bodyRef,
    pointOnWall = false,
    boundaryLines,
    occluders,
    maxOvershoot = 0,
    exactSegWallIds
  } = params;
  const circle = mainArc.createCircle();

  let closestIntersectPointInfo: IIntersectPointInfo | undefined;
  function calIntersectInfo(tValue: number) {
    const intersects: IIntersectPointInfo[] = [];
    subSegInfos.forEach(({ seg, wallId }, _index) => {
      // 与子边的「延长形态」(直线→无限直线 Line2d，弧→整圆 Circle2d) 求交：
      // 端点斜接的角点常落在相邻墙边段端点之外约一个壁厚处，用有界边段会漏掉它（左右偏移不对称时尤其明显）；
      // 改用延长形态可稳定找到该角点，再由 searchClosestIntersectPoint 按扫角取离连接点最近的真实角点。
      const extendedSeg =
        seg instanceof LineSeg2d
          ? new Line2d({ points: { p1: seg.start, p2: seg.end } })
          : seg instanceof ArcSeg2d
            ? new Circle2d({ center: seg.center, radius: seg.radius })
            : seg;
      let _intersects = intersectCommonShape(circle, extendedSeg, tValue);
      if (!_intersects) return;
      // 圆与弧相交有可能是两个交点
      if (_intersects.length === 2) {
        if (Point2dUtils.equals(_intersects[0], _intersects[1], arcThresholdValue)) {
          _intersects.splice(1, 1);
        }
      }

      // 子边用「延长形态」(弧→整圆 / 线→无限直线)求交，会得到落在子边真实范围之外很远的「幻影」交点
      // (整圆另一侧 / 无限直线远端，尤其是端帽短线延长后)。斜接角点最多落在子边端点外约一个墙厚处，
      // 远端幻影则差出一大截。丢弃明显超出子边真实范围的幻影，避免端盖错误地切到相邻墙的另一端 / 延长线上。
      // 越界余量必须是【绝对长度】(maxOvershoot ≈ 2×墙厚)：写成「一段子边长度」会随子边尺寸失控 ——
      // 7348 长的房间墙允许越界 7348，于是弧墙端点落在墙角、偏移弧与该墙【延长线】573 处的交点被当成真交点，
      // 端盖沿房间外的空地平切掉 573 —— 即"起点切的不对"。
      // 厚 0 边界的子边是精确的：它没有端盖、也没有斜接填充，线段到头之外没有材料，所以不给越界余量
      // (只留几何精度级别的 EXACT_SEG_TOL)。此前一律用 maxOvershoot(2×墙厚)，弧起点落在窄槽角上时
      // 会把「槽底线延长线」上的点当成真切点，把没被任何东西挡住的那条偏移拉过去 2~5mm。
      const overshoot = exactSegWallIds?.has(wallId) ? EXACT_SEG_TOL : maxOvershoot;
      if (seg instanceof ArcSeg2d) {
        const marginRad = seg.radius > 0 ? overshoot / seg.radius : 0;
        _intersects = _intersects.filter((p) => {
          const sw = seg.getPointSweepAngle(p);
          return !(sw > seg.getSweepAngle() + marginRad && sw < 2 * Math.PI - marginRad);
        });
        if (!_intersects.length) return;
      } else if (seg instanceof LineSeg2d) {
        const ax = seg.start.x;
        const ay = seg.start.y;
        const bx = seg.end.x;
        const by = seg.end.y;
        const len2 = (bx - ax) ** 2 + (by - ay) ** 2;
        if (len2 > 0) {
          const tMargin = overshoot / Math.sqrt(len2);
          _intersects = _intersects.filter((p) => {
            const t = ((p.x - ax) * (bx - ax) + (p.y - ay) * (by - ay)) / len2;
            return t > -tMargin && t < 1 + tMargin; // 真实段 t∈[0,1]，两端各留 maxOvershoot 的斜接余量
          });
          if (!_intersects.length) return;
        }
      }

      const curIntersectInfos: IIntersectPointInfo[] = [];
      _intersects.forEach((p) => {
        const segAngle = getAngle(getTangentDirection(seg, p));
        curIntersectInfos.push({
          point: p,
          index: _index,
          angle: segAngle,
          wallId,
          intersectCount: _intersects.length
        });
      });
      const curClosestIntersectPointInfo = searchClosestIntersectPoint(
        handleType,
        curIntersectInfos,
        mainArc,
        isLeft,
        subCenterMap,
        bodyRef,
        pointOnWall,
        boundaryLines,
        occluders,
        maxOvershoot
      );
      if (curClosestIntersectPointInfo) {
        intersects.push(curClosestIntersectPointInfo);
      }
    });

    if (intersects.length)
      // 搜索和mainArc扫角最接近的点
      closestIntersectPointInfo = searchClosestIntersectPoint(
        handleType,
        intersects,
        mainArc,
        isLeft,
        subCenterMap,
        bodyRef,
        pointOnWall,
        boundaryLines,
        occluders,
        maxOvershoot
      );
  }

  calIntersectInfo(arcThresholdValue);

  // todo
  // 计算到的交点与对于连接点的距离
  if (closestIntersectPointInfo) {
    const intersectConnectDistance = Point2dUtils.distanceTo(
      closestIntersectPointInfo.point,
      handleType === HandleType.Start ? mainArc.start : mainArc.end
    );
    if (intersectConnectDistance > mainArc.radius) {
      //可能存在一些因误差而丢失的点
      calIntersectInfo(0.1);
    }
  }

  return closestIntersectPointInfo
    ? { point: closestIntersectPointInfo.point, index: closestIntersectPointInfo.index }
    : null;
}

function searchClosestIntersectPoint(
  handleType: HandleType,
  intersectInfos: IIntersectPointInfo[],
  mainArc: ArcSeg2d,
  isLeft: boolean,
  subCenterMap?: Map<string, Circle2d | Line2d>,
  bodyRef?: IPoint2d,
  pointOnWall: boolean = false,
  boundaryLines?: Line2d[],
  occluders?: { seg: IShapeSeg2d; halfWidth: number }[],
  maxOvershoot: number = 0
) {
  const circle = mainArc.createCircle();
  const clockwise = mainArc.clockwise;

  let minAngleDiff = Infinity;
  let closestInSpan = false;
  let closestIntersectPointInfo: IIntersectPointInfo | undefined;

  function calMinSweepAngle(intersectPointInfo: IIntersectPointInfo) {
    // 计算扫角
    const intersectPointAngle = circle.getPointAngle(intersectPointInfo.point);
    let intersectSweepAngle = 0;

    if (handleType === HandleType.End && !clockwise) {
      intersectSweepAngle = intersectPointAngle - mainArc.startAngle;
    } else if (handleType === HandleType.End && clockwise) {
      intersectSweepAngle = mainArc.startAngle - intersectPointAngle;
    } else if (handleType === HandleType.Start && clockwise) {
      intersectSweepAngle = intersectPointAngle - mainArc.endAngle;
    } else if (handleType === HandleType.Start && !clockwise) {
      intersectSweepAngle = mainArc.endAngle - intersectPointAngle;
    }

    if (intersectSweepAngle === 0) return;
    intersectSweepAngle = normalizeAngle(intersectSweepAngle);
    const diffSweep = Math.abs(intersectSweepAngle - mainArc.getSweepAngle());
    // 交点是否落在弧本体跨角内(未超出弧的扫角)
    const inSpan = intersectSweepAngle <= mainArc.getSweepAngle() + thresholdValue;

    // 主弧也是按「延长形态」(整圆)求交的：落在弧本体之外的交点最多是端点外约一个墙厚处的斜接角点，
    // 超出更多就是圆上的幻影(常与真交点关于端点对称、扫角差并列，仅靠 inSpan 优先级选不掉它)。
    // 按【绝对长度】(maxOvershoot)硬剔除：出本端与绕回远端两侧都量，取到最近弧端点的角距离折算成弧长。
    if (!inSpan && maxOvershoot > 0 && mainArc.radius > 0) {
      const outsideRad = Math.min(intersectSweepAngle - mainArc.getSweepAngle(), 2 * Math.PI - intersectSweepAngle);
      if (outsideRad * mainArc.radius > maxOvershoot) return;
    }

    // 扫角差更小者优先；扫角差并列时(相切处关于连接点对称的跨内/跨外两交点)，优先取跨内的真实交点，
    // 避免因遍历顺序误选弧延长线上的跨外幻影点。
    const better =
      diffSweep < minAngleDiff - thresholdValue ||
      (Math.abs(diffSweep - minAngleDiff) <= thresholdValue && inSpan && !closestInSpan);
    if (better) {
      minAngleDiff = diffSweep;
      closestInSpan = inSpan;
      closestIntersectPointInfo = intersectPointInfo;
    }
  }

  // applyOccluder=true 时剔除「埋在相邻厚墙实心带内部」的交点。但多条厚墙在接头处重叠时(如近相切的两条弧)，
  // 正确的近接头切点会同时落在另一条墙带内 → 全被剔除、只剩远端幻影。故把 occluder 做成「软偏好」：
  // 先按剔除跑一遍；若一个都没选到，再不剔除跑一遍，保住近处真实切点而不是跳到远端幻影。
  const consider = (applyOccluder: boolean) => {
    intersectInfos.forEach((intersectPointInfo) => {
      // 房间/结构墙边界：落在房间墙外侧(与主墙本体 bodyRef 异侧、超出容差)的交点一律剔除(硬过滤)。
      if (boundaryLines && boundaryLines.length && bodyRef) {
        const outside = boundaryLines.some((bl) => {
          const sideP = signedSideOfCenterline(intersectPointInfo.point, bl);
          const sideBody = signedSideOfCenterline(bodyRef, bl);
          return Math.abs(sideP) > thresholdValue && sideP * sideBody < 0;
        });
        if (outside) return;
      }

      // 埋在相连厚墙实心带内部的交点(软剔除：仅第一遍)：合法端盖顶点一般落在墙体「边线」上，不埋进另一条墙里。
      // (交点落在某墙边线上时到该墙中线恰为半宽=带边界，不算"内部"，不会被它自己剔除。)
      if (
        applyOccluder &&
        occluders &&
        occluders.some((oc) => isInsideBand(intersectPointInfo.point, oc.seg, oc.halfWidth))
      )
        return;

      // 判断弧线是穿进墙还是穿出墙，过滤掉出墙
      // 默认墙体轮廓按顺时针绘制，轮廓线左侧为外部，右侧为内部
      let arcDirectionOnIntersectPoint = getTangentDirection(mainArc, intersectPointInfo.point); // 交点在主弧线上的方向
      if (handleType === HandleType.Start) arcDirectionOnIntersectPoint = opposite(arcDirectionOnIntersectPoint);
      const segDirectionOnIntersectPoint = getDirectionFromAngle(intersectPointInfo.angle); // 线段切线方向
      const segLeftDirection = rotate(segDirectionOnIntersectPoint, Math.PI / 2);
      // intersectCount === 1 说明弧墙与该边相切，不过滤；count > 1 时过滤掉"出墙"的一侧，保留拼接侧交点。
      const _dot = dot(arcDirectionOnIntersectPoint, segLeftDirection);
      const subCenter = subCenterMap?.get(intersectPointInfo.wallId);
      if (subCenter && bodyRef && (subCenter instanceof Circle2d || pointOnWall)) {
        // 侧判定明确：与主墙本体异侧的交点一律丢弃
        const sideP = signedSideOfCenterline(intersectPointInfo.point, subCenter);
        const sideBody = signedSideOfCenterline(bodyRef, subCenter);
        if (Math.abs(sideP) > thresholdValue && sideP * sideBody < 0) return;
        // 「相切处关于连接点对称的跨内/跨外两交点，丢弃跨外的远端幻影」这条判据统一由 calMinSweepAngle 里的
        // 绝对量闸(outsideRad×半径 > maxOvershoot)执行。此处原先另写了一条固定 0.2rad 的版本，
        // 固定角度会随半径放大(r=4710 时 0.2rad = 942mm)，端点身后 573mm 的幻影就漏了过去。
      } else {
        // 端点端点相连 + 直墙：dot 斜接判据；相切(count===1)不过滤
        const isExiting = handleType === HandleType.Start ? _dot < thresholdValue : _dot > -thresholdValue;
        if (isExiting && intersectPointInfo.intersectCount > 1) return;
      }

      calMinSweepAngle(intersectPointInfo);
    });
  };

  consider(true);
  if (!closestIntersectPointInfo) consider(false);

  return closestIntersectPointInfo;
}

function extendedLineLineIntersectSegments(
  params: IExtendedSegIntersectSegsParams<LineSeg2d, LineSeg2d>
): IExtendedSegIntersectSegsResult | null {
  const { mainSeg: mainLine, subSegInfos, handleType } = params;
  const segs = subSegInfos.map(({ seg }) => seg);
  const ray = handleType === HandleType.Start ? mainLine.createEndRay() : mainLine.createStartRay();

  let minDistance = Infinity;
  let point;
  let index = -1;
  segs.forEach((seg, _index) => {
    const intersect = intersectRayAndLineSeg(ray, seg);
    if (!intersect) return;
    const distance = Point2dUtils.distanceTo(ray.origin, intersect);
    if (distance < minDistance) {
      minDistance = distance;
      point = intersect;
      index = _index;
    }
  });

  return point ? { point, index } : null;
}

function extendedLineIntersectSegments(
  params: IExtendedSegIntersectSegsParams<LineSeg2d>
): IExtendedSegIntersectSegsResult | null {
  const { mainSeg, subSegInfos, handleType, originEnclosed } = params;
  const nearStart = handleType === HandleType.Start;
  const ray = nearStart ? mainSeg.createEndRay() : mainSeg.createStartRay();
  const intersects: { point: IPoint2d; index: number; angle: number; wallId: string }[] = [];
  subSegInfos.forEach(({ seg, wallId }, _index) => {
    const _intersects = intersectCommonShape(ray, seg);
    if (!_intersects) return;
    _intersects.forEach((p) => {
      const samePointOtherWallIndex = intersects.findIndex(({ point, wallId: _wallId }) => {
        return Point2dUtils.equals(point, p) && _wallId !== wallId;
      });

      if (samePointOtherWallIndex !== -1) {
        intersects.splice(samePointOtherWallIndex, 1);
        return;
      }

      intersects.push({ point: p, index: _index, angle: getAngle(getTangentDirection(seg, p)), wallId });
    });
  });

  if (!intersects.length) return null;

  const compareDistance = Point2dUtils.distanceTo(mainSeg.start, mainSeg.end);
  let minDistance = Infinity;
  let point;
  let index = -1;
  let mainSegDirection = Point2dUtils.getUnitDirection(mainSeg.start, mainSeg.end);
  if (nearStart) mainSegDirection = opposite(mainSegDirection);

  // originEnclosed：本偏移边在接头那头的端点已经埋在别的东西里(厚墙实心带 / 厚 0 边界围出的实心区)。
  // 埋着的话，合法端盖点只能是它「沿本墙 body 方向穿出那块材料」的出口(即 d ≤ 全长那一侧)；
  // 落在接头【身后】的交点会把埋着的那一截留在外面、压在别人身上。
  // 下面的排序用 |全长 - d|，对「身后」和「向墙内」是对称的 —— 接头附近若有个身后的掠过点(薄片/弧带的边界)，
  // 它会以更小的 |全长 - d| 击败真正的前向出口(实测身后 43mm 的幻影赢了前向 117mm 的真出口)，
  // 端盖于是从那个幻影点起跳、PathFinder 再沿更早墙的整条 body 绕一圈回来，拼出自交轮廓。
  // 注意不能一律禁止「身后」：端点没被埋住时，身后的交点正是锐角接头填充反侧楔形所需的斜接点。
  const originBuried = !!originEnclosed;

  intersects.forEach(({ point: _p, angle, index: _segIndex }) => {
    const tangentDirection = getDirectionFromAngle(angle);
    const leftDirection = Point2dUtils.rotateDirection(tangentDirection, Math.PI / 2);

    if (dot(leftDirection, mainSegDirection) >= 0) return;

    const rayDistance = Point2dUtils.distanceTo(ray.origin, _p);
    if (originBuried && rayDistance > compareDistance + thresholdValue) return; // 埋着 → 只认向墙内的出口
    const distance = Math.abs(compareDistance - rayDistance);
    if (distance < minDistance) {
      minDistance = distance;
      point = _p;
      index = _segIndex;
    }
  });

  return point ? { point, index } : null;
}
